const KEY = 'dq-catalog-person-view'
const LAST_KEY = 'dq-catalog-last-view'
const ZOOM_MIN = 0.4
const ZOOM_MAX = 3

export type SystemFitMode = 'fit' | 'width' | 'fixed-width' | 'height'

export const SYSTEM_FIT_MODES: Array<{ id: SystemFitMode; label: string }> = [
  { id: 'fit', label: 'Ganz' },
  { id: 'width', label: 'Volle Breite' },
  { id: 'fixed-width', label: 'Feste Breite' },
  { id: 'height', label: 'Feste Höhe' },
]

export type PersonView = {
  systemZoom: number
  systemFit: SystemFitMode
  systemScrollLeft: number
  systemScrollTop: number
  pdfScale: number
  pdfScrollLeft: number
  pdfScrollTop: number
}

export type LastView = {
  systemZoom: number
  systemFit: SystemFitMode
  pdfScale: number
}

export const DEFAULT_PERSON_VIEW: PersonView = {
  systemZoom: 1,
  systemFit: 'fit',
  systemScrollLeft: 0,
  systemScrollTop: 0,
  pdfScale: 1,
  pdfScrollLeft: 0,
  pdfScrollTop: 0,
}

export const DEFAULT_LAST_VIEW: LastView = {
  systemZoom: 1,
  systemFit: 'fit',
  pdfScale: 1,
}

function clampFit(value: unknown): SystemFitMode {
  if (value === 'width' || value === 'fixed-width' || value === 'height' || value === 'fit') return value
  return 'fit'
}

function clampZoom(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return 1
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n * 10) / 10))
}

function clampScroll(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n < 0) return 0
  return n
}

function normalize(raw: Partial<PersonView> | null | undefined): PersonView {
  return {
    systemZoom: clampZoom(raw?.systemZoom),
    systemFit: clampFit(raw?.systemFit),
    systemScrollLeft: clampScroll(raw?.systemScrollLeft),
    systemScrollTop: clampScroll(raw?.systemScrollTop),
    pdfScale: clampZoom(raw?.pdfScale),
    pdfScrollLeft: clampScroll(raw?.pdfScrollLeft),
    pdfScrollTop: clampScroll(raw?.pdfScrollTop),
  }
}

const pending = new Map<string, Partial<PersonView>>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

function readStore(): Record<string, Partial<PersonView>> {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed as Record<string, Partial<PersonView>>
  } catch {
    return {}
  }
}

export function readLastView(): LastView {
  try {
    const raw = localStorage.getItem(LAST_KEY)
    if (!raw) return DEFAULT_LAST_VIEW
    const parsed = JSON.parse(raw) as Partial<LastView>
    return {
      systemZoom: clampZoom(parsed?.systemZoom),
      systemFit: clampFit(parsed?.systemFit),
      pdfScale: clampZoom(parsed?.pdfScale),
    }
  } catch {
    return DEFAULT_LAST_VIEW
  }
}

export function writeLastView(patch: Partial<LastView>): LastView {
  const next = { ...readLastView(), ...patch, systemFit: clampFit(patch.systemFit ?? readLastView().systemFit) }
  next.systemZoom = clampZoom(next.systemZoom)
  next.pdfScale = clampZoom(next.pdfScale)
  try {
    localStorage.setItem(LAST_KEY, JSON.stringify(next))
  } catch {
    return next
  }
  return next
}

function storedRecord(teacherId: string): Partial<PersonView> | null {
  const raw = readStore()[teacherId]
  if (!raw || typeof raw !== 'object') return null
  return raw
}

function baseFor(teacherId: string): PersonView {
  const last = readLastView()
  const inherited = {
    ...DEFAULT_PERSON_VIEW,
    systemZoom: last.systemZoom,
    systemFit: last.systemFit,
    pdfScale: last.pdfScale,
  }
  const raw = storedRecord(teacherId)
  if (!raw) return inherited
  return normalize({ ...inherited, ...raw })
}

export function resolvePersonView(teacherId: string): PersonView {
  return normalize({ ...baseFor(teacherId), ...pending.get(teacherId) })
}

export function readPersonView(teacherId: string): PersonView | null {
  const raw = storedRecord(teacherId)
  const queued = pending.get(teacherId)
  if (!raw && !queued) return null
  return normalize({ ...baseFor(teacherId), ...queued })
}

export function writePersonView(teacherId: string, patch: Partial<PersonView>): PersonView {
  const queued = pending.get(teacherId)
  pending.delete(teacherId)
  const store = readStore()
  const next = normalize({ ...baseFor(teacherId), ...queued, ...patch })
  store[teacherId] = next
  const lastPatch: Partial<LastView> = {}
  if ('systemZoom' in patch) lastPatch.systemZoom = next.systemZoom
  if ('systemFit' in patch) lastPatch.systemFit = next.systemFit
  if ('pdfScale' in patch) lastPatch.pdfScale = next.pdfScale
  if (Object.keys(lastPatch).length > 0) writeLastView(lastPatch)
  try {
    localStorage.setItem(KEY, JSON.stringify(store))
  } catch {
    return next
  }
  return next
}

export function queuePersonView(teacherId: string, patch: Partial<PersonView>) {
  pending.set(teacherId, { ...pending.get(teacherId), ...patch })
  if (flushTimer != null) clearTimeout(flushTimer)
  flushTimer = setTimeout(() => {
    flushTimer = null
    flushPersonViews()
  }, 200)
}

export function flushPersonViews() {
  if (flushTimer != null) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  if (pending.size === 0) return
  const store = readStore()
  for (const [teacherId, patch] of pending) {
    store[teacherId] = normalize({ ...baseFor(teacherId), ...patch })
  }
  pending.clear()
  try {
    localStorage.setItem(KEY, JSON.stringify(store))
  } catch {
    return
  }
}
